import { createInertiaApp } from "@inertiajs/vue3";
import { type DefineComponent, createApp, h, markRaw } from "vue";

import { useNetworkStatus } from "@/composables/use-network-status";
import { initializeTheme } from "@/hooks/use-appearance";
import PersistentLayout from "@/layouts/persistent-layout";
import PublicLayout from "@/layouts/public-layout";

const appName = String(import.meta.env.VITE_APP_NAME ?? "Rails");

if (typeof window !== "undefined") {
  useNetworkStatus();
  navigator.storage?.persist?.();
}

void createInertiaApp({
  // Rails 8 `params.expect(... attachments: [])` requires bracket array keys (`field[]`).
  // This must be passed as Inertia defaults because createInertiaApp resets config at boot.
  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
    },
  },
  title: (title) => (title ? `${title} - ${appName}` : appName),


  resolve: (name) => {
    const pages = import.meta.glob<{ default: DefineComponent & { layout?: unknown } }>(
      "../pages/**/*.vue",
      {
        eager: true,
      },
    );
    const pagePath = `../pages/${name}.vue`;
    const fallbackPath = "../pages/_migration-placeholder.vue";
    const page = pages[pagePath] ?? pages[fallbackPath];

    if (!page) {
      throw new Error(
        `Missing Inertia page component: '${name}.vue' and missing fallback page '${fallbackPath}'.`,
      );
    }

    if (!pages[pagePath]) {
      console.error(
        `Missing Inertia page component: '${name}.vue'. Rendering migration placeholder instead.`,
      );
    }

    // Use different layouts based on page path
    // Public pages use PublicLayout, all other pages use PersistentLayout
    // see https://inertia-rails.dev/guide/pages#default-layouts
    //
    const isPublicPage =
      name.startsWith("public/") ||
      name === "sessions/new" ||
      name === "users/new" ||
      name.startsWith("sessions/passwordlesses/") ||
      name.startsWith("identity/password_resets/");

    page.default.layout ??= isPublicPage
      ? markRaw(PublicLayout)
      : markRaw(PersistentLayout);

    return page;
  },

  setup({ el, App, props, plugin }) {
    // Uncomment the following to enable SSR hydration:
    // if (el.hasChildNodes()) {
    //   hydrateRoot(el, h(App, props))
    //   return
    // }
    createApp({ render: () => h(App, props) }).use(plugin).mount(el);
  },

  progress: {
    color: "#22c55e",
    showSpinner: false,
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error;
  } else {
    console.error(
      "Missing root element.\n\n" +
        "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
        'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
    );
  }
});

// This will set light / dark mode on load...
initializeTheme();
